/**
 * SECTION_SELECTION_MODAL.JSX - Section Selection Modal for Copy/Move Items
 * 
 * SUMMARY:
 * Modal component for selecting target section when copying or moving variation items.
 * Displays hierarchical section structure with indentation for nested sections.
 * Filters sections based on hierarchy rules (only shows sections that allow items).
 * 
 * PROPS:
 * @param {boolean} open - Whether modal is open
 * @param {Function} onClose - Close modal handler
 * @param {Array} sections - Array of section items from subTaskArray
 * @param {Function} onSelectSection - Callback when section is selected (parentId)
 * @param {string|null} currentParentId - Current parent ID to exclude from list
 * @param {string} mode - 'copy' or 'move' mode
 * @param {Function} getAllowedChildType - Function to check what can be added to a section
 * 
 * @returns {JSX.Element} Section selection modal
 */

import React from 'react';
import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';
import './SectionSelectionModal.scss';

export default function SectionSelectionModal({
    open,
    onClose,
    sections,
    onSelectSection,
    currentParentId,
    mode = 'copy',
    getAllowedChildType
}) {
    /**
     * Build hierarchical section list for display
     * Returns array of sections with level for indentation
     */
    const buildSectionList = () => {
        const sectionItems = sections.filter(item => item.is_section);
        const list = [];

        const addSectionWithChildren = (section, level = 0) => {
            list.push({ ...section, level });

            // Find children of this section
            const children = sectionItems.filter(
                item => item.parent_subtask_id === section.id
            );

            children.forEach(child => addSectionWithChildren(child, level + 1));
        };

        // Add root-level sections first
        const rootSections = sectionItems.filter(item => !item.parent_subtask_id);
        rootSections.forEach(section => addSectionWithChildren(section, 0));

        return list;
    };

    const sectionList = buildSectionList();

    /**
     * Check if a section allows items to be added
     * Uses hierarchy validation to ensure we don't violate rules
     */
    const canAddItemToSection = (parentId) => {
        if (!getAllowedChildType) return true; // If no validation function, allow all
        const allowed = getAllowedChildType(parentId);
        return allowed.canAddItem;
    };

    // Filter sections to only show valid targets
    const validSections = sectionList.filter(section => canAddItemToSection(section.id));

    // Check if root level allows items
    const rootAllowsItems = canAddItemToSection(null);

    const handleSectionClick = (parentId) => {
        onSelectSection(parentId);
        onClose();
    };

    return (
        <Modal
            show={open}
            onHide={onClose}
            centered
            className="section-selection-modal"
            data-cy="section-selection-modal"
        >
            <Modal.Header closeButton>
                <Modal.Title data-cy="section-selection-modal-title">
                    {mode === 'copy' ? 'Copy Item To Section' : 'Move Item To Section'}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <div className="section-list">
                    {/* Root option - only if current parent is not root and root allows items */}
                    {currentParentId !== null && rootAllowsItems && (
                        <div
                            className="section-item root-section"
                            onClick={() => handleSectionClick(null)}
                            data-cy="section-option-root"
                        >
                            <span className="section-name">Root (No Section)</span>
                        </div>
                    )}

                    {/* Section list - filtered by hierarchy rules */}
                    {validSections.length > 0 ? (
                        validSections
                            .filter(section => section.id !== currentParentId)
                            .map(section => (
                                <div
                                    key={section.id}
                                    className="section-item"
                                    style={{ paddingLeft: `${section.level * 20 + 12}px` }}
                                    onClick={() => handleSectionClick(section.id)}
                                    data-cy={`section-option-${section.id}`}
                                >
                                    <span className="section-name">
                                        {section.description || '(Unnamed Section)'}
                                    </span>
                                </div>
                            ))
                    ) : (
                        <div className="no-sections" data-cy="no-sections-message">
                            {!rootAllowsItems && currentParentId === null
                                ? 'No valid sections available. Items can only be added to sections that don\'t already contain nested sections.'
                                : 'No other sections available that allow items'
                            }
                        </div>
                    )}
                </div>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={onClose} data-cy="section-modal-cancel">
                    Cancel
                </Button>
            </Modal.Footer>
        </Modal>
    );
}

